import { motion } from 'framer-motion'
import { HeartIcon } from '@heroicons/react/24/solid'
import { SparklesIcon } from '@heroicons/react/24/outline'

export default function HeroSection({ t, language }) {
  const scrollToCapture = () => {
    document.getElementById('email-capture')?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <section className="section-padding min-h-[90vh] flex items-center justify-center">
      <div className="max-w-6xl mx-auto text-center">
        {/* Main Headline */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-6"
        >
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-display font-bold text-neutral-900 mb-4">
            {t.hero.headline}
          </h1>
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-display font-medium text-gradient-warm">
            {t.hero.subheadline}
          </h2>
        </motion.div>

        {/* Description */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-xl md:text-2xl text-neutral-600 mb-12 max-w-3xl mx-auto leading-relaxed"
        >
          {t.hero.description}
        </motion.p>

        {/* Mock Phone Interface */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mb-12 max-w-md mx-auto"
        >
          <div className="bg-white rounded-[3rem] shadow-2xl p-8 border-8 border-neutral-800">
            {/* Mock Screen */}
            <div className="bg-warm-cream rounded-3xl p-8 aspect-[9/16] flex flex-col items-center justify-center relative overflow-hidden">
              {/* Decorative background */}
              <div className="absolute inset-0 bg-pattern-dots opacity-30"></div>
              
              {/* Content */}
              <div className="relative z-10 text-center">
                <motion.div
                  animate={{ scale: [1, 1.05, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="mb-6"
                >
                  <HeartIcon className="h-20 w-20 text-primary-500 mx-auto" />
                </motion.div>
                
                <h3 className="text-2xl font-bold text-neutral-800 mb-4">
                  {language === 'ja' ? '元気です' : "I'm here"}
                </h3>
                
                {/* Big Button */}
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-full bg-primary-500 hover:bg-primary-600 text-white font-bold py-6 rounded-3xl shadow-lg text-xl"
                >
                  {language === 'ja' ? 'タップして送信' : 'Tap to Send'}
                </motion.button>
                
                <p className="text-sm text-neutral-500 mt-4">
                  {language === 'ja' ? '最終チェックイン: 今日 08:30' : 'Last check-in: Today 08:30'}
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="flex flex-col sm:flex-row gap-4 justify-center items-center"
        >
          <button
            onClick={scrollToCapture}
            className="btn-primary flex items-center gap-2 text-lg"
          >
            <SparklesIcon className="h-6 w-6" />
            {t.hero.cta}
          </button>
          
          <div className="flex flex-col items-center sm:items-start">
            <span className="text-sm text-neutral-500 mb-1">
              {language === 'ja' ? '料金' : 'Pricing'}
            </span>
            <span className="text-2xl font-bold text-neutral-800">
              {t.hero.monthlyPrice}
            </span>
          </div>
        </motion.div>

        {/* Trust Badge */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-12 flex items-center justify-center gap-2 text-sm text-neutral-500"
        >
          <HeartIcon className="h-5 w-5 text-primary-400" />
          <span>
            {language === 'ja' 
              ? '先着100名様 - 創設メンバー価格 ¥500/月' 
              : 'First 100 people - Founding member price ¥500/month'}
          </span>
        </motion.div>
      </div>
    </section>
  )
}
